<template>
  <div class="fullPage">
    <page :title="title" :breadcrumb="breadcrumb" :load="doInit">
      <v-layout row wrap>
        <v-flex md12 lg8>
          <v-card class="queryPnl">
            <card-avatar :fname="userForm.firstName" :lname="userForm.lastName">
              <div slot="action" v-if="!newUser">
                <v-tooltip top v-if="user.acctInactive">
                  <v-btn slot="activator" icon :loading="submitting" @click="promptReactivateExpired = true">
                    <v-icon color="red">mdi-timer-off</v-icon>
                  </v-btn>
                  <span>User Account has expired</span>
                </v-tooltip>
                <v-tooltip top v-if="user.acctBlocked">
                  <v-btn slot="activator" icon @click="doPromptUnblock" :loading="submitting">
                    <v-icon color="red">mdi-block-helper</v-icon>
                  </v-btn>
                  <span>User account is blocked from login</span>
                </v-tooltip>
                <v-tooltip top v-if="user.acctDisabled">
                  <v-btn slot="activator" icon @click="doPromptToggleEnable" :loading="submitting">
                    <v-icon color="red">mdi-lock</v-icon>
                  </v-btn>
                  <span>User account is disabled</span>
                </v-tooltip>
                <v-tooltip top v-else>
                  <v-btn slot="activator" icon @click="doPromptToggleEnable" :loading="submitting">
                    <v-icon color="green">mdi-lock-open</v-icon>
                  </v-btn>
                  <span>User account is not disabled</span>
                </v-tooltip>
                <v-tooltip top v-if="!newUser">
                  <v-btn slot="activator" icon :loading="submitting" @click="promptResetPass = true">
                    <v-icon color="orange">mdi-textbox-password</v-icon>
                  </v-btn>
                  <span>Reset Password</span>
                </v-tooltip>
                <v-tooltip top>
                  <v-btn slot="activator" icon @click="doQuery">
                    <v-icon color="primary">mdi-magnify</v-icon>
                  </v-btn>
                  <span>Query User</span>
                </v-tooltip>
              </div>
            </card-avatar>
            <v-card-text>
              <message :messages="messages"></message>
              <form>
                <v-container fluid grid-list-lg>
                  <v-layout row wrap>
                    <v-flex md8 xs12>
                      <v-text-field :label="labels.firstName" v-model="userForm.firstName" :error-messages="errors.collect('firstName')" v-validate="'required|max:128|name'" data-vv-name="firstName" :data-vv-as="labels.firstName"></v-text-field>
                    </v-flex>
                    <v-flex md4 xs12>
                      <v-text-field :label="labels.lastName" v-model="userForm.lastName" :error-messages="errors.collect('lastName')" v-validate="'required|max:128|name'" data-vv-name="lastName" :data-vv-as="labels.lastName"></v-text-field>
                    </v-flex>
                    <v-flex md6 xs12>
                      <v-text-field :label="labels.mobileNo" v-model="userForm.mobileNo" type="tel" :error-messages="errors.collect('mobileNo')" v-validate="'required|numeric|max:8'" data-vv-name="mobileNo" :data-vv-as="labels.mobileNo"></v-text-field>
                    </v-flex>
                    <v-flex md6 xs12>
                      <v-text-field :label="labels.officeNo" v-model="userForm.officeNo" type="tel" :error-messages="errors.collect('officeNo')" v-validate="'numeric|max:15'" data-vv-name="officeNo" :data-vv-as="labels.officeNo"></v-text-field>
                    </v-flex>
                    <v-flex xs12>
                      <v-text-field :label="labels.email" v-model="userForm.email" type="email" :error-messages="errors.collect('email')" v-validate="'required|email'" data-vv-name="email" :data-vv-as="labels.email"></v-text-field>
                    </v-flex>
                    <v-flex xs12>
                      <v-text-field :label="labels.usrRefId" v-model="userForm.usrRefId" :error-messages="errors.collect('usrRefId')" v-validate="'required|max:20'" data-vv-name="usrRefId" :data-vv-as="labels.usrRefId"></v-text-field>
                    </v-flex>
                    <v-flex xs12>
                      <v-checkbox :label="labels.isIntermediary" v-model="user.isIntermediary" hide-details @change="doIntermediary"></v-checkbox>
                    </v-flex>
                    <v-flex xs12 v-if="user.isIntermediary">
                      <v-autocomplete :items="intermediaryList" v-model="userForm.intermediary" :label="labels.intermediary" item-text="label" item-value="value" data-vv-name="intermediary" :data-vv-as="labels.intermediary" :error-messages="errors.collect('intermediary')" v-validate="'required'"></v-autocomplete>
                    </v-flex>
                    <v-flex xs12>
                      <v-checkbox :label="labels.superAdmin" v-model="userForm.superAdmin" hide-details></v-checkbox>
                    </v-flex>
                    <v-flex xs12 v-if="!userForm.superAdmin">
                      <v-autocomplete v-model="userForm.roles" :label="labels.roles" multiple chips :items="roles" item-text="label" item-value="value">
                        <template slot="selection" slot-scope="data">
                          <v-chip close @input="data.parent.selectItem(data.item.value)" :selected="data.selected" class="chip--select-multi" :key="JSON.stringify(data.item.value)">
                            {{ data.item.label }}
                          </v-chip>
                        </template>
                      </v-autocomplete>
                    </v-flex>
                    <v-flex xs12>
                      <v-btn color="primary" type="submit" :loading="submitting" @click.prevent="doSave">
                        {{newUser? 'Add User' : 'Update User'}}
                        <v-icon right>{{newUser? 'mdi-account-plus' : 'mdi-content-save'}}</v-icon>
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-container>
              </form>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </page>
    <confirm v-model="promptReactivateExpired" @ok="doReactivateExpiredUser" title="Reactivate User"
      label="Are you sure you want to reactivate this expired user account?"></confirm>
    <confirm v-model="promptDisable" :label="disabledLabel" @ok="doToggleEnableUser"></confirm>
    <confirm v-model="promptUnblock" @ok="doUnblock">
      This user is blocked from the following IP Address(es). Do you wish to remove the block(s)?
      <lazy-div :load="getBlockedIP" class="blockedIPPnl" :card="false">
        <ul>
          <li v-for="ipAddress in user.blockedIP" :key="ipAddress">{{ipAddress}}</li>
        </ul>
      </lazy-div>
    </confirm>
    <confirm v-model="promptResetPass" title="Reset Password" label="Are you sure you want to reset this user's password? A reset password will send to you." @ok="doResetPassword"></confirm>
    <!-- <v-dialog v-model="showPassword" width="500">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          Password Reset
        </v-card-title>
        <v-card-text>
          <div v-if="copySuccess == true" class="green--text">
          Your password successful copy to your clipboard
          </div>
          <div v-if="copySuccess == false" class="red--text">
            Your password unsucessful copy to your clipboard
          </div>
          <v-spacer></v-spacer>
          <v-text-field v-model="newPassword" box readonly label="New Password" ref="NewPassword" @focus="$event.target.select()" append-outer-icon="mdi-content-copy" @click:append-outer="copyPassword"></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="showPassword = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog> -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { expandURI } from '@/utils/ajax'
import CardAvatar from '@/components/reusable/CardAvatar'
import MessageMixin from '@/mixins/message'
import { extractMenu } from '@/utils/extract'

export default {
  data() {
    return {
      breadcrumb: [],
      labels: {
        firstName: 'First Name*',
        lastName: 'Last Name*',
        mobileNo: 'Mobile No.*',
        officeNo: 'Office/Ext No.',
        email: 'Email*',
        superAdmin: 'Is Super Administrator?',
        roles: 'Access Roles',
        isIntermediary: 'Is Intermediary?',
        intermediary: 'Intermediary*',
        usrRefId: 'User Reference ID*'
      },
      userForm: {
        userId: undefined,
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        mobileNo: undefined,
        officeNo: undefined,
        superAdmin: false,
        roles: [],
        intermediary: undefined,
        usrRefId: undefined,
        revision: undefined
      },
      user: {
        passwordChange: undefined,
        passwordDate: undefined,
        acctDisabled: undefined,
        createdDate: undefined,
        acctBlocked: undefined,
        acctInactive: undefined,
        blockedIP: [],
        isIntermediary: false
      },
      menu: [],
      roles: [],
      newUser: false,
      submitting: false,
      promptDisable: false,
      promptUnblock: false,
      promptResetPass: false,
      promptReactivateExpired: false,
      disabledLabel: '',
      showPassword: false,
      newPassword: null,
      copySuccess: null
    }
  },
  computed: {
    ...mapGetters([
      'derieveBreadcrumbByName'
    ]),
    title() {
      if (this.newUser) {
        return 'New User'
      } else {
        return 'Review User'
      }
    },
    intermediaryList() {
      return this.$store.state.adminUser.intermediaryList
    }
  },
  methods: {
    copyPassword() {
      var selection = this.$refs.NewPassword
      try {
        selection.focus()
        this.copySuccess = document.execCommand('copy') // run command to copy selected text to clipboard
      } catch (error) {
        this.copySuccess = false
      }
    },
    async doInit() {
      if (!this.newUser) {
        // Review
        await Promise.all([this.reviewUser(), this.getRoles()])
      } else {
        await this.getRoles()
      }
    },
    async reviewUser() {
      try {
        const apiURI = this.$store.state.adminUser.menu.userGet
        const expand = {
          id: this.id
        }
        const result = await this.$http.get(expandURI(apiURI, expand))
        const { data } = result.data
        if (data) {
          // User data init
          this.userForm.userId = data.user.userId
          this.userForm.firstName = data.user.firstName
          this.userForm.lastName = data.user.lastName
          this.userForm.email = data.user.email
          this.userForm.mobileNo = data.user.mobileNo
          this.userForm.officeNo = data.user.officeNo
          this.userForm.revision = data.user.revision

          this.user.passwordChange = data.user.passwordChange
          this.user.passwordDate = data.user.passwordDate
          this.user.acctDisabled = data.user.acctDisabled
          this.user.acctInactive = data.user.acctInactive
          this.user.createdDate = data.user.createdDate
          this.user.acctBlocked = data.user.acctBlocked

          this.userForm.intermediary = data.user.intermediary
          if (data.user.intermediary) {
            this.user.isIntermediary = true
          } else {
            this.user.isIntermediary = false
          }
          this.userForm.superAdmin = data.user.superAdmin
          this.$set(this.userForm, 'roles', data.user.roles)

          this.userForm.usrRefId = data.user.usrRefId
          // Extract menu
          this.menu = extractMenu(result.data._links)
        }
      } catch (error) {
        throw error
      }
    },
    async getRoles() {
      const apiURI = this.$store.state.adminUser.menu.roles
      try {
        const result = await this.$http.get(apiURI)
        const { data } = result
        if (data) {
          this.roles = data.data.roles
        }
      } catch (err) {
        throw err
      }
    },
    doIntermediary() {
      if (!this.user.isIntermediary) {
        this.userForm.intermediary = undefined
      }
    },
    doQuery() {
      this.$router.push({ name: 'userQuery', params: { id: this.id } })
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.newUser) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doUpdate() {
      const apiURI = this.menu.update
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.put(apiURI, this.userForm)
          const { data } = result

          if (data) {
            // Show message via notify
            this.notify(data)

            // Redirect back to listing
            this.$router.push({ name: 'users' })
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.submitting = false
        }
      } else {
        this.errorMessage('Invalid API URL')
      }
    },
    async doCreate() {
      const apiURI = this.$store.state.adminUser.menu.userCreate
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.userForm)
          const { data } = result

          if (data) {
            // Show message via notify
            this.notify(data)

            // Redirect back to listing
            this.$router.push({ name: 'users' })
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.submitting = false
        }
      } else {
        this.errorMessage('Invalid API URL')
      }
    },
    doPromptUnblock() {
      this.promptUnblock = true
    },
    doPromptToggleEnable() {
      if (this.user.acctDisabled) {
        this.disabledLabel = 'Do you want to enable this user?'
      } else {
        this.disabledLabel = 'Do you want to disable this user?'
      }
      this.promptDisable = true
    },
    async doReactivateExpiredUser() {
      this.submitting = true
      const apiURI = this.menu.reactivateUser
      if (apiURI) {
        const expand = {
          id: this.id
        }
        try {
          const result = await this.$http.post(expandURI(apiURI, expand))
          if (result.data) {
            this.$router.push({ name: 'users' })

            this.$snotify.success('Successfully reactivated user account')
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      }
      this.submitting = false
      this.promptReactivateExpired = false
    },
    async doToggleEnableUser() {
      let apiURI
      if (this.user.acctDisabled) {
        apiURI = this.menu.enable
      } else {
        apiURI = this.menu.disable
      }
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI)
          const { data } = result

          if (data) {
            // Show message via notify
            this.notify(data)

            this.user.acctDisabled = !this.user.acctDisabled
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.submitting = false
        }
      } else {
        this.errorMessage('Invalid API URL')
      }
    },
    async getBlockedIP() {
      try {
        const result = await this.$http.get(this.menu.blockedIP)
        const { data } = result
        if (data) {
          this.user.blockedIP = data.data
        }
      } catch (err) {
        throw err
      }
    },
    async doUnblock() {
      const apiURI = this.menu.unblock
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI)
          const { data } = result

          if (data) {
            this.message(data)
            this.user.acctBlocked = false
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.submitting = false
        }
      } else {
        this.errorMessage('Invalid API URL')
      }
    },
    async doResetPassword() {
      const apiURI = this.menu.resendPasswordMail
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI)
          const { data } = result

          if (data) {
            this.message(data)
            // this.showPassword = true
            // this.newPassword = data.data
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.submitting = false
        }
      } else {
        this.errorMessage('Invalid API URL')
      }
    }
  },
  beforeMount() {
    if (this.id === 'new') {
      this.newUser = true
    }
    // Manual breadcrumb
    this.breadcrumb = this.derieveBreadcrumbByName('users')
    this.breadcrumb.push({
      name: 'userReview',
      text: this.title
    })
  },
  props: ['id'],
  components: {
    CardAvatar
  },
  mixins: [
    MessageMixin
  ]
}
</script>

<style lang="scss">
.queryPnl {
  margin-top: 50px;
}
.blockedIPPnl {
  padding: 20px;
}
</style>
